import React from 'react';
import { getTeamLogo, normalizeTeamName } from '../utils/teamUtils';

// Format round name function
const formatRoundName = (roundName) => {
  if (!roundName) return '';
  return roundName.replace('Round ', '');
};

// Create a function to get unique games sorted by year and round
const getUniqueGames = (games) => {
  // Create a map to store unique games
  const uniqueGames = new Map();
  
  // Process each game
  games.forEach(game => {
    const key = `${game.year}_${game.round}`;
    if (!uniqueGames.has(key)) {
      uniqueGames.set(key, game);
    }
  });

  // Convert to array and sort
  return Array.from(uniqueGames.values())
    .sort((a, b) => {
      // Sort by year first (descending)
      const yearDiff = parseInt(b.year) - parseInt(a.year);
      if (yearDiff !== 0) return yearDiff;
      
      // Then by round number (descending)
      const roundA = parseInt(a.round.replace(/\D/g, '')) || 0;
      const roundB = parseInt(b.round.replace(/\D/g, '')) || 0;
      return roundB - roundA;
    });
};

const PlayerRecentGames = ({ players, playerId, activeFilters, getFilteredGames, selectedSeasons }) => {
  if (!players || !playerId) return null;

  // Define columns we want to show
  const columns = [
    { key: 'year', label: 'Year', sticky: true },
    { key: 'round', label: 'Round', sticky: true },
    { key: 'opponent', label: 'Opp', sticky: true },
    { key: 'venue', label: 'Venue' },
    { key: 'all_runs', label: 'Runs' },
    { key: 'all_run_metres', label: 'Metres' },
    { key: 'post_contact_metres', label: 'PCM' },
    { key: 'line_breaks', label: 'LB' },
    { key: 'line_break_assists', label: 'LBA' },
    { key: 'performance_points', label: 'PP' }
  ];

  // Get filtered games and sort them
  const filteredGames = getFilteredGames(players, playerId, activeFilters)
    .filter(p => selectedSeasons.includes(parseInt(p.year)));
  
  // Get unique games
  const games = getUniqueGames(filteredGames);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
        <span className="text-sm font-medium text-gray-900 dark:text-white">Game Log</span>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-900/50">
              {columns.map(col => (
                <th key={col.key} className={`
                  py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400
                  ${col.sticky ? 'sticky left-0 z-10 bg-gray-50 dark:bg-gray-900/50' : ''}
                `}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {games.map((game, index) => {
              const opponent = game.is_home_game ? game.away_team : game.home_team;
              const opponentLogo = getTeamLogo(normalizeTeamName(opponent));
              
              return (
                <tr key={`${game.year}_${game.round}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  {columns.map(col => (
                    <td key={col.key} className={`
                      py-2 px-3 text-xs text-gray-700 dark:text-gray-300
                      ${col.sticky ? 'sticky left-0 z-10 bg-white dark:bg-gray-800' : ''}
                    `}>
                      {col.key === 'opponent' ? (
                        <div className="flex items-center gap-2">
                          <img 
                            src={opponentLogo} 
                            alt={opponent}
                            className="w-4 h-4 object-contain"
                            onError={(e) => { e.target.style.display = 'none' }}
                          />
                          <span>{opponent} {game.is_home_game ? '(H)' : '(A)'}</span>
                        </div>
                      ) : col.key === 'round' ? (
                        formatRoundName(game[col.key])
                      ) : col.key === 'performance_points' ? (
                        (() => {
                          let pp = 0;
                          pp += (parseFloat(game['points']) || 0) * 4;
                          pp += (parseFloat(game['try_assists']) || 0) * 10;
                          const runMeters = parseFloat(game['all_run_metres']) || 0;
                          pp += Math.floor(runMeters / 10);
                          pp += (parseFloat(game['line_breaks']) || 0) * 5;
                          pp += (parseFloat(game['tackles_made']) || 0);
                          return pp.toFixed(0);
                        })()
                      ) : game[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlayerRecentGames;